import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { SearchClear, pickerItemId, pickerItemLabel, pickerMatches } from './SearchablePicker';

const itemId = (item, index) => {
    if (item == null) return String(index);
    if (item.id !== undefined && item.id !== null && item.id !== '') return String(item.id);
    if (item._id !== undefined && item._id !== null && item._id !== '') return String(item._id);
    return String(index);
};

const itemLabel = (item) => pickerItemLabel(item) || item?.n || item?.title || '';

const PcSelect = ({
    value = '',
    onChange,
    onBlur,
    options = [],
    placeholder = 'Не указано',
    disabled,
    className = '',
    searchable,
    searchPlaceholder = 'Найти',
    emptyText = 'Ничего не найдено',
    allowEmpty = false,
    emptyLabel,
    mono,
    disablePortal = false,
    id,
    'aria-label': ariaLabel,
}) => {
    const wrapRef = useRef(null);
    const menuRef = useRef(null);
    const searchRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [hi, setHi] = useState(0);
    const [pos, setPos] = useState(null);

    const list = useMemo(() => {
        const rows = (options || []).filter(Boolean).map((item, index) => ({
            id: itemId(item, index),
            name: itemLabel(item) || itemId(item, index),
            meta: item.login || item.code || '',
            disabled: !!item.disabled,
            raw: item,
        }));
        if (allowEmpty) {
            return [{ id: '', name: emptyLabel || placeholder, meta: '', disabled: false, raw: null }, ...rows];
        }
        return rows;
    }, [options, allowEmpty, emptyLabel, placeholder]);

    const selected = list.find((item) => item.id === String(value ?? '')) || null;
    const useSearch = searchable === true || (searchable !== false && list.length > 8);
    const matches = useMemo(() => {
        if (!useSearch || !query.trim()) return list;
        return list.filter((item) => pickerMatches(
            { id: item.id, name: item.name, login: item.meta },
            query,
            pickerItemLabel,
            (row) => row.login || '',
            pickerItemId
        ));
    }, [list, query, useSearch]);

    const emit = (next) => {
        if (!onChange) return;
        onChange({ target: { value: next } });
    };

    const close = useCallback((picked) => {
        setOpen(false);
        setQuery('');
        if (!picked && onBlur) onBlur({ target: { value: value ?? '' } });
    }, [onBlur, value]);

    const pick = (item) => {
        if (!item || item.disabled) return;
        emit(item.id);
        close(true);
    };

    const place = () => {
        const node = wrapRef.current;
        if (!node) return;
        const rect = node.getBoundingClientRect();
        const maxH = 240;
        const below = window.innerHeight - rect.bottom - 8;
        const above = rect.top - 8;
        const openUp = below < 160 && above > below;
        setPos({
            left: Math.max(8, Math.min(rect.left, window.innerWidth - Math.max(rect.width, 180) - 8)),
            width: Math.max(rect.width, 180),
            top: openUp ? undefined : rect.bottom + 4,
            bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
            maxHeight: Math.min(maxH, openUp ? above : below),
        });
    };

    useLayoutEffect(() => {
        if (!open) return undefined;
        place();
        const onWin = (event) => {
            if (event && menuRef.current && menuRef.current.contains(event.target)) return;
            place();
        };
        window.addEventListener('resize', onWin);
        window.addEventListener('scroll', onWin, true);
        return () => {
            window.removeEventListener('resize', onWin);
            window.removeEventListener('scroll', onWin, true);
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;
        setHi(Math.max(0, list.findIndex((item) => item.id === String(value ?? ''))));
    }, [open, value, list]);

    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (event) => {
            if (wrapRef.current && wrapRef.current.contains(event.target)) return;
            if (menuRef.current && menuRef.current.contains(event.target)) return;
            close();
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open, close]);

    useEffect(() => {
        if (open && useSearch && searchRef.current) searchRef.current.focus();
    }, [open, useSearch]);

    const onKeyDown = (event) => {
        if (disabled) return;
        if (!open && (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            setOpen(true);
            return;
        }
        if (!open) return;
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setHi((index) => Math.min(index + 1, Math.max(0, matches.length - 1)));
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHi((index) => Math.max(0, index - 1));
        } else if (event.key === 'Enter') {
            event.preventDefault();
            pick(matches[hi] || matches[0]);
        } else if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            close();
        }
    };

    return (
        <div className={`pc-sel${mono ? ' is-mono' : ''}${className ? ` ${className}` : ''}${open ? ' is-open' : ''}`} ref={wrapRef}>
            <button
                id={id}
                type="button"
                className={`pc-sel-btn${selected && selected.id ? '' : ' is-ph'}`}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={ariaLabel}
                onClick={() => {
                    if (disabled) return;
                    setOpen((prev) => !prev);
                }}
                onKeyDown={onKeyDown}
            >
                <span className="v">{selected ? selected.name : placeholder}</span>
                <svg className="pc-sel-ch" viewBox="0 0 10 6" fill="none" aria-hidden="true">
                    <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" />
                </svg>
            </button>
            {open && (disablePortal || pos) ? (() => {
                const drop = (
                <div
                    ref={menuRef}
                    className={`pc-sel-drop${disablePortal ? ' is-local' : ''}`}
                    role="listbox"
                    style={disablePortal ? { maxHeight: 240 } : {
                        left: pos.left,
                        width: pos.width,
                        top: pos.top,
                        bottom: pos.bottom,
                        maxHeight: pos.maxHeight,
                    }}
                >
                    {useSearch ? (
                        <div className="pc-pick-srch pc-sel-srch">
                            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
                                <circle cx="7" cy="7" r="4.5" />
                                <path d="M10.5 10.5L14 14" />
                            </svg>
                            <input
                                ref={searchRef}
                                className="pc-pick-inp"
                                value={query}
                                placeholder={searchPlaceholder}
                                onChange={(event) => {
                                    setQuery(event.target.value);
                                    setHi(0);
                                }}
                                onKeyDown={onKeyDown}
                            />
                            <SearchClear visible={Boolean(query)} onClear={() => { setQuery(''); setHi(0); }} />
                        </div>
                    ) : null}
                    <div className="pc-sel-list">
                        {matches.length ? matches.map((item, index) => (
                            <button
                                type="button"
                                key={`${item.id}-${index}`}
                                role="option"
                                aria-selected={item.id === String(value ?? '')}
                                className={`${item.id === String(value ?? '') ? 'is-on' : ''}${index === hi ? ' is-hi' : ''}`}
                                disabled={item.disabled}
                                onMouseEnter={() => setHi(index)}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => pick(item)}
                            >
                                <span>{item.name}</span>
                                {item.meta && item.meta !== item.name ? <i>{item.meta}</i> : null}
                            </button>
                        )) : (
                            <div className="pc-pick-empty">{emptyText}</div>
                        )}
                    </div>
                </div>
                );
                return disablePortal ? drop : createPortal(drop, document.body);
            })() : null}
        </div>
    );
};

export default PcSelect;
